import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Store, Car, Home as HomeIcon, Check, ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import client, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";

const STORES = [
  { code: "walmart", name: "Walmart" }, { code: "costco", name: "Costco" },
  { code: "superstore", name: "Superstore" }, { code: "nofrills", name: "No Frills" },
  { code: "safeway", name: "Safeway" }, { code: "sobeys", name: "Sobeys" },
  { code: "saveonfoods", name: "Save-On-Foods" }, { code: "freshco", name: "FreshCo" },
  { code: "coop", name: "Calgary Co-op" },
];
const LOYALTY = ["PC Optimum", "Scene+", "More Rewards", "Co-op Membership", "Costco Membership"];
const DIET = ["Vegetarian", "Vegan", "Gluten-free", "Dairy-free", "Halal", "Kosher", "Nut-free"];

export default function Onboarding() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [d, setD] = useState({
    city: "", postal_code: "", home_address: "", home_lat: null, home_lng: null,
    preferred_stores: [], consider_all: true,
    max_radius: 20, max_stores: 3, warehouse_ok: true, loyalty_programs: ["PC Optimum"],
    preferred_brands: [], dietary_restrictions: [], generic_ok: true, convenience_weight: 0.5,
    household_size: 2, weekly_budget: 180, shopping_frequency: "Weekly",
  });
  const up = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const toggle = (k, v) => setD((p) => ({ ...p, [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v] }));

  const steps = ["Welcome", "Location", "Stores", "Preferences", "Household", "Done"];

  const finish = async () => {
    setBusy(true);
    try {
      const { home_lat, home_lng, city, postal_code, home_address, ...rest } = d;
      await client.post("/onboarding", { data: { ...rest, ...(home_address ? { home_address } : {}) } });
      try {
        if (home_lat && home_lng) await client.post("/live/locate", { lat: home_lat, lng: home_lng });
        else if ((postal_code || city || home_address).trim().length >= 3) await client.post("/live/locate-text", { query: (postal_code || home_address || city).trim() });
      } catch { toast.info("We couldn't place that location — you can set it from Home"); }
      await refresh();
      toast.success("You're all set!");
      navigate("/list", { replace: true });
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Location not supported");
    navigator.geolocation.getCurrentPosition(
      (pos) => { up("home_lat", pos.coords.latitude); up("home_lng", pos.coords.longitude); toast.success("Location captured"); },
      () => toast.error("Location denied — enter your address instead")
    );
  };

  const Progress = () => (
    <div className="flex items-center gap-1.5 mb-8">
      {steps.map((s, i) => (
        <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-pp-forest" : "bg-pp-border"}`} />
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-pp-cream flex flex-col">
      <div className="p-5 flex justify-center"><Logo size={40} /></div>
      <div className="flex-1 flex items-start sm:items-center justify-center px-4 pb-10">
        <div className="w-full max-w-lg bg-white rounded-3xl pp-shadow-lg p-6 sm:p-8 pp-fade-up" data-testid="onboarding-card">
          <Progress />

          {step === 0 && (
            <div className="text-center space-y-4">
              <div className="mx-auto h-16 w-16 grid place-items-center rounded-2xl bg-[#EBF7EE]"><Store className="h-8 w-8 text-pp-forest" /></div>
              <h1 className="text-3xl font-extrabold text-pp-text">Your groceries. Your route. Your best price.</h1>
              <p className="text-pp-muted">Pantry Pilot compares your complete grocery list across stores and builds the best shopping plan — balancing price, promos, package size and drive time.</p>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5">
              <div><h2 className="text-2xl font-extrabold text-pp-text">Where do you shop?</h2>
                <p className="text-pp-muted text-sm mt-1">We use your location only to find nearby stores and estimate drive time. It stays private.</p></div>
              <Button onClick={useMyLocation} data-testid="use-location" className="w-full h-11 rounded-full bg-pp-forest">
                <MapPin className="h-4 w-4 mr-2" /> Use my current location
              </Button>
              <div className="grid gap-3">
                <div><Label>Address</Label><Input className="mt-1 bg-white h-11" data-testid="onb-address" value={d.home_address} onChange={(e) => up("home_address", e.target.value)} placeholder="Street address (optional)" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Postal code</Label><Input className="mt-1 bg-white h-11" data-testid="onb-postal" value={d.postal_code} onChange={(e) => up("postal_code", e.target.value)} placeholder="e.g. M5V 2T6" /></div>
                  <div><Label>City</Label><Input className="mt-1 bg-white h-11" value={d.city} onChange={(e) => up("city", e.target.value)} /></div>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <div><h2 className="text-2xl font-extrabold text-pp-text">Which stores do you visit?</h2>
                <p className="text-pp-muted text-sm mt-1">Pick your regulars — you can consider all nearby stores too.</p></div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {STORES.map((s) => {
                  const on = d.preferred_stores.includes(s.code);
                  return (
                    <button key={s.code} data-testid={`onb-store-${s.code}`} onClick={() => toggle("preferred_stores", s.code)}
                      className={`rounded-2xl border-2 p-3 text-sm font-semibold text-left transition-all ${on ? "border-pp-forest bg-[#EBF7EE] text-pp-forest" : "border-pp-border bg-white text-pp-text hover:border-pp-fresh"}`}>
                      <div className="flex items-center justify-between">{s.name}{on && <Check className="h-4 w-4" />}</div>
                    </button>
                  );
                })}
              </div>
              <label className="flex items-center justify-between rounded-2xl bg-pp-cream p-3">
                <span className="text-sm font-medium text-pp-text">Consider all nearby stores</span>
                <Switch checked={d.consider_all} onCheckedChange={(v) => up("consider_all", v)} data-testid="onb-consider-all" />
              </label>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <h2 className="text-2xl font-extrabold text-pp-text">Shopping preferences</h2>
              <div>
                <div className="flex justify-between text-sm font-medium"><span>Max driving radius</span><span className="text-pp-forest font-bold">{d.max_radius} km</span></div>
                <Slider className="mt-2" min={2} max={40} step={1} value={[d.max_radius]} onValueChange={([v]) => up("max_radius", v)} data-testid="onb-radius" />
              </div>
              <div>
                <div className="flex justify-between text-sm font-medium"><span>Max stores per trip</span><span className="text-pp-forest font-bold">{d.max_stores}</span></div>
                <Slider className="mt-2" min={1} max={5} step={1} value={[d.max_stores]} onValueChange={([v]) => up("max_stores", v)} data-testid="onb-maxstores" />
              </div>
              <div>
                <div className="flex justify-between text-sm font-medium"><span>Savings vs. convenience</span><span className="text-pp-forest font-bold">{d.convenience_weight < 0.4 ? "Max savings" : d.convenience_weight > 0.6 ? "Max convenience" : "Balanced"}</span></div>
                <Slider className="mt-2" min={0} max={1} step={0.1} value={[d.convenience_weight]} onValueChange={([v]) => up("convenience_weight", v)} data-testid="onb-convenience" />
              </div>
              <label className="flex items-center justify-between rounded-2xl bg-pp-cream p-3">
                <span className="text-sm font-medium">Include warehouse clubs (Costco)</span>
                <Switch checked={d.warehouse_ok} onCheckedChange={(v) => up("warehouse_ok", v)} data-testid="onb-warehouse" />
              </label>
              <label className="flex items-center justify-between rounded-2xl bg-pp-cream p-3">
                <span className="text-sm font-medium">Generic / store brands are OK</span>
                <Switch checked={d.generic_ok} onCheckedChange={(v) => up("generic_ok", v)} data-testid="onb-generic" />
              </label>
              <div>
                <Label className="text-sm">Loyalty programs you use</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {LOYALTY.map((l) => (
                    <button key={l} onClick={() => toggle("loyalty_programs", l)} data-testid={`onb-loyalty-${l}`}
                      className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${d.loyalty_programs.includes(l) ? "bg-pp-forest text-white border-pp-forest" : "bg-white border-pp-border text-pp-text"}`}>{l}</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5">
              <h2 className="text-2xl font-extrabold text-pp-text">About your household <span className="text-pp-muted text-base font-normal">(optional)</span></h2>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Household size</Label><Input type="number" min={1} className="mt-1 bg-white h-11" data-testid="onb-household" value={d.household_size} onChange={(e) => up("household_size", +e.target.value)} /></div>
                <div><Label>Weekly budget ($)</Label><Input type="number" className="mt-1 bg-white h-11" data-testid="onb-budget" value={d.weekly_budget} onChange={(e) => up("weekly_budget", +e.target.value)} /></div>
              </div>
              <div>
                <Label>Dietary restrictions</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {DIET.map((x) => (
                    <button key={x} onClick={() => toggle("dietary_restrictions", x)}
                      className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${d.dietary_restrictions.includes(x) ? "bg-pp-fresh text-pp-text border-pp-fresh" : "bg-white border-pp-border text-pp-text"}`}>{x}</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="text-center space-y-4">
              <div className="mx-auto h-16 w-16 grid place-items-center rounded-2xl bg-[#EBF7EE] pp-pop"><Check className="h-8 w-8 text-pp-forest" /></div>
              <h1 className="text-3xl font-extrabold text-pp-text">You're all set!</h1>
              <p className="text-pp-muted">Let's build your first grocery list and find your best plan.</p>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between gap-3">
            {step > 0 ? (
              <Button variant="ghost" onClick={() => setStep(step - 1)} data-testid="onb-back" className="rounded-full"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
            ) : <span />}
            {step < 5 ? (
              <Button onClick={() => setStep(step + 1)} data-testid="onb-next" className="rounded-full bg-pp-forest px-6">Continue <ArrowRight className="h-4 w-4 ml-1" /></Button>
            ) : (
              <Button onClick={finish} disabled={busy} data-testid="onb-finish" className="rounded-full bg-pp-forest px-6">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create my first list"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
